/*
 * Yomiri Plugin Storage & Persistence
 * Manages installed plugins in local storage with reactive change notifications.
 */

import type { InstalledPlugin, YomiriPluginManifest } from './PluginTypes.ts';

const STORAGE_KEY = 'yomiri_installed_plugins';
export const EVENT_PLUGINS_CHANGED = 'yomiri-plugins-changed';

export class PluginStore {
  /**
   * Retrieves all installed plugins from storage.
   */
  public static getInstalled(): Record<string, InstalledPlugin> {
    try {
      if (typeof localStorage === 'undefined') return {};
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      return parsed;
    } catch (err) {
      console.warn('[PluginStore] Failed to read installed plugins:', err);
      return {};
    }
  }

  /**
   * Returns an array of only the currently enabled plugins.
   */
  public static getEnabled(): InstalledPlugin[] {
    return Object.values(this.getInstalled()).filter(p => p.enabled);
  }

  /**
   * Saves or updates an installed plugin in storage.
   */
  public static save(plugin: InstalledPlugin): void {
    const current = this.getInstalled();
    current[plugin.manifest.id] = {
      ...plugin,
      lastUpdated: Date.now(),
    };
    this.persist(current);
  }

  /**
   * Removes a plugin by its ID.
   */
  public static remove(pluginId: string): boolean {
    const current = this.getInstalled();
    if (pluginId in current) {
      delete current[pluginId];
      this.persist(current);
      return true;
    }
    return false;
  }

  /**
   * Toggles the enabled/disabled state of a plugin.
   */
  public static toggleEnabled(pluginId: string, enabled?: boolean): boolean {
    const current = this.getInstalled();
    if (pluginId in current) {
      current[pluginId].enabled = enabled !== undefined ? enabled : !current[pluginId].enabled;
      this.persist(current);
      return current[pluginId].enabled;
    }
    return false;
  }

  /**
   * Updates configuration properties for a plugin.
   */
  public static updateConfig(pluginId: string, config: Record<string, any>): void {
    const current = this.getInstalled();
    if (pluginId in current) {
      current[pluginId].config = {
        ...(current[pluginId].config || {}),
        ...config,
      };
      this.persist(current);
    }
  }

  /**
   * Checks if a plugin with the given ID is installed.
   */
  public static has(pluginId: string): boolean {
    const current = this.getInstalled();
    return pluginId in current;
  }

  /**
   * Writes the full record to localStorage and notifies reactive listeners.
   */
  private static persist(data: Record<string, InstalledPlugin>): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      }
      if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
        window.dispatchEvent(new CustomEvent(EVENT_PLUGINS_CHANGED, { detail: data }));
      }
    } catch (err) {
      console.error('[PluginStore] Failed to persist plugins:', err);
    }
  }
}
